import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Download, FileImage, FileText, Eye, AlertTriangle, 
  CheckCircle2, XCircle, ZoomIn, ZoomOut, RotateCcw,
  File, Loader2, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('kaha_session');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const FACE_LABELS = {
  front: 'Frente', back: 'Atrás', left: 'Izquierda',
  right: 'Derecha', top: 'Arriba', all: 'Todas las caras',
};

// ============ DIELINE SVG RENDERER ============
const DielineSVG = ({ data, artworkUrl, zoom, onZoomChange }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    onZoomChange(prev => Math.max(0.3, Math.min(4, prev + delta)));
  }, [onZoomChange]);

  if (!data) return null;

  const { sheet_width, sheet_height, margin, glue_flap, body_y, body_height,
    flap_height, panels, print_side, dimensions } = data;

  const gfTaper = 5;
  const rightEdge = panels[panels.length - 1].x + panels[panels.length - 1].width;

  const faceLabels = { front: 'FRENTE', right: 'DERECHA', back: 'ATRÁS', left: 'IZQUIERDA' };
  const faceMap = { front: 0, right: 1, back: 2, left: 3 };

  const targetPanelIdxs = print_side === 'all'
    ? [0, 1, 2, 3]
    : print_side in faceMap ? [faceMap[print_side]] : [];

  const handleMouseDown = (e) => {
    if (e.button === 0) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };
  const handleMouseMove = (e) => {
    if (isPanning) setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
  };
  const handleMouseUp = () => setIsPanning(false);

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden bg-[#2a2a2e] rounded-lg border border-border"
      style={{ height: 420, cursor: isPanning ? 'grabbing' : 'grab' }}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      data-testid="dieline-preview-container"
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${sheet_width} ${sheet_height}`}
        style={{
          width: '100%', height: '100%',
          transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
          transformOrigin: 'center center',
        }}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Background */}
        <rect x="0" y="0" width={sheet_width} height={sheet_height} fill="#3a3a3e" />

        {/* White panel fills */}
        {panels.map((p, i) => (
          <g key={`fill-${i}`}>
            <rect x={p.x} y={margin} width={p.width} height={flap_height} fill="#fff" />
            <rect x={p.x} y={body_y} width={p.width} height={body_height} fill="#fff" />
            <rect x={p.x} y={body_y + body_height} width={p.width} height={flap_height} fill="#fff" />
          </g>
        ))}

        {/* Glue flap fill */}
        <polygon
          points={`${margin + gfTaper},${body_y} ${margin + glue_flap},${body_y} ${margin + glue_flap},${body_y + body_height} ${margin + gfTaper},${body_y + body_height}`}
          fill="#f5f5f5"
        />

        {/* Artwork zones */}
        {targetPanelIdxs.map(idx => {
          const p = panels[idx];
          if (!p) return null;
          const artW = p.width - 4;
          const artH = body_height - 4;
          return (
            <g key={`art-${idx}`}>
              <rect x={p.x + 2} y={body_y + 2} width={artW} height={artH}
                fill="#E8F5E9" stroke="#4CAF50" strokeWidth="0.3" strokeDasharray="1.5,1.5" opacity="0.6" />
              {artworkUrl ? (
                <image
                  href={artworkUrl}
                  x={p.x + 2} y={body_y + 2}
                  width={artW} height={artH}
                  preserveAspectRatio="xMidYMid meet"
                  opacity="0.85"
                />
              ) : (
                <text x={p.x + p.width / 2} y={body_y + body_height / 2}
                  textAnchor="middle" dominantBaseline="middle"
                  fill="#4CAF50" fontSize="3" opacity="0.7">
                  ARTWORK
                </text>
              )}
            </g>
          );
        })}

        {/* CREASE lines (dashed blue) */}
        <g stroke="#3B82F6" strokeWidth="0.3" strokeDasharray="2,2" fill="none">
          {panels.map((p, i) => (
            <g key={`crease-${i}`}>
              <line x1={p.x} y1={margin} x2={p.x} y2={sheet_height - margin} />
              <line x1={p.x} y1={body_y} x2={p.x + p.width} y2={body_y} />
              <line x1={p.x} y1={body_y + body_height} x2={p.x + p.width} y2={body_y + body_height} />
            </g>
          ))}
          <line x1={rightEdge} y1={margin} x2={rightEdge} y2={sheet_height - margin} />
          <line x1={margin + glue_flap} y1={body_y} x2={margin + glue_flap} y2={body_y + body_height} />
        </g>

        {/* CUT lines (solid red) */}
        <g stroke="#EF4444" strokeWidth="0.3" fill="none">
          {panels.map((p, i) => (
            <g key={`cut-${i}`}>
              <rect x={p.x} y={margin} width={p.width} height={flap_height} />
              <rect x={p.x} y={body_y + body_height} width={p.width} height={flap_height} />
            </g>
          ))}
          <polygon
            points={`${margin + gfTaper},${body_y} ${margin + glue_flap},${body_y} ${margin + glue_flap},${body_y + body_height} ${margin + gfTaper},${body_y + body_height}`}
          />
          <line x1={panels[0].x} y1={body_y} x2={panels[0].x} y2={body_y + body_height} />
          <line x1={rightEdge} y1={body_y} x2={rightEdge} y2={body_y + body_height} />
        </g>

        {/* Dimension labels */}
        <g fill="#94a3b8" fontSize="2.8" textAnchor="middle" fontFamily="monospace">
          {/* Total width top */}
          <line x1={margin} y1={margin - 6} x2={rightEdge} y2={margin - 6} stroke="#94a3b8" strokeWidth="0.15" />
          <text x={(margin + rightEdge) / 2} y={margin - 8}>
            {(2 * dimensions.length + 2 * dimensions.width + glue_flap).toFixed(0)} mm
          </text>

          {/* Panel widths bottom */}
          {panels.map((p, i) => {
            const label = p.face === 'front' || p.face === 'back' ? 'L' : 'W';
            return (
              <text key={`dim-${i}`} x={p.x + p.width / 2} y={sheet_height - margin + 5} fontSize="2.2">
                {label}={p.width.toFixed(0)}
              </text>
            );
          })}
          <text x={margin + glue_flap / 2} y={sheet_height - margin + 5} fontSize="2.2">
            GF={glue_flap.toFixed(0)}
          </text>

          {/* Height right */}
          <text x={rightEdge + 6} y={body_y + body_height / 2}
            transform={`rotate(-90, ${rightEdge + 6}, ${body_y + body_height / 2})`} fontSize="2.5">
            H={body_height.toFixed(0)}
          </text>

          {/* Flap height */}
          <text x={rightEdge + 12} y={margin + flap_height / 2}
            transform={`rotate(-90, ${rightEdge + 12}, ${margin + flap_height / 2})`} fontSize="2.2">
            W/2={flap_height.toFixed(0)}
          </text>

          {/* Face labels */}
          {panels.map((p, i) => (
            <text key={`face-${i}`} x={p.x + p.width / 2} y={body_y + body_height - 3}
              fontSize="2" opacity="0.35">
              {faceLabels[p.face]}
            </text>
          ))}
        </g>
      </svg>

      {/* Legend overlay */}
      <div className="absolute bottom-2 left-2 flex gap-3 bg-black/60 backdrop-blur-sm rounded px-3 py-1.5 text-[10px]">
        <span className="flex items-center gap-1">
          <span className="w-3 h-0.5 bg-red-500 inline-block" /> Corte
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-0.5 bg-blue-500 inline-block border-dashed border-b border-blue-500" style={{ borderStyle: 'dashed' }} /> Pliegue
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-1.5 bg-green-500/40 inline-block" /> Arte
        </span>
      </div>
    </div>
  );
};

// ============ MAIN COMPONENT ============
const ArtworkDieline = ({ orderId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [artworkUrl, setArtworkUrl] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [downloading, setDownloading] = useState(null);
  const [showArtworkModal, setShowArtworkModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/admin/orders/${orderId}/dieline/data`, { headers: getAuthHeaders() });
        setData(res.data);

        // Load artwork image for overlay
        const artFileId = res.data.artwork?.file_id;
        if (artFileId && res.data.artwork.content_type?.startsWith('image/')) {
          setArtworkUrl(`${API}/files/${artFileId}`);
        }
      } catch (err) {
        console.error('Error loading dieline data:', err);
      }
      setLoading(false);
    };
    fetchData();
  }, [orderId]);

  const handleDownload = async (type) => {
    setDownloading(type);
    try {
      const res = await axios.get(`${API}/admin/orders/${orderId}/dieline/${type}`, {
        responseType: 'blob',
        headers: getAuthHeaders(),
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = type === 'pdf' ? 'pdf' : type === 'svg' ? 'svg' : 'png';
      link.download = `KAHA-Dieline-${data?.order_number || orderId.slice(0, 8)}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Archivo ${type.toUpperCase()} descargado`);
    } catch (err) {
      toast.error(`Error al descargar ${type.toUpperCase()}`);
    }
    setDownloading(null);
  };

  const handleDownloadArtwork = async () => {
    if (!data?.artwork?.file_id) return;
    setDownloading('artwork');
    try {
      const res = await axios.get(`${API}/files/${data.artwork.file_id}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = data.artwork.filename || 'artwork';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Artwork original descargado');
    } catch (err) {
      toast.error('Error al descargar artwork');
    }
    setDownloading(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-accent" />
        <span className="ml-2 text-sm text-muted-foreground">Generando dieline...</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center p-6 text-muted-foreground text-sm">
        No se pudo generar el dieline para esta orden.
      </div>
    );
  }

  const hasArtwork = !!data.artwork;
  const warnings = data.warnings || [];
  const dims = data.dieline?.dimensions || {};

  return (
    <div className="space-y-4" data-testid="artwork-dieline-section">
      {/* Header Row */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-primary flex items-center gap-2">
          <FileImage className="w-4 h-4" />
          Artwork y Dieline — FEFCO 0201
        </h3>
        <span className="text-xs text-muted-foreground font-mono">
          {dims.length?.toFixed(0)} x {dims.width?.toFixed(0)} x {dims.height?.toFixed(0)} mm
        </span>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="space-y-1.5">
          {warnings.map((w, i) => (
            <div key={`${w.severity}-${w.message.slice(0, 20)}`} className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs ${
              w.severity === 'error' 
                ? 'bg-red-50 text-red-700 border border-red-200'
                : 'bg-amber-50 text-amber-700 border border-amber-200'
            }`} data-testid={`dieline-warning-${i}`}>
              {w.severity === 'error' ? <XCircle className="w-3.5 h-3.5 flex-shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />}
              {w.message}
            </div>
          ))}
        </div>
      )}

      {/* Artwork Info */}
      {hasArtwork && (
        <div className="flex items-center justify-between bg-muted/40 rounded-lg px-3 py-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded bg-accent/10 flex items-center justify-center">
              {data.artwork.content_type?.startsWith('image/') ? (
                <ImageIcon className="w-4 h-4 text-accent" />
              ) : (
                <File className="w-4 h-4 text-accent" />
              )}
            </div>
            <div>
              <p className="text-xs font-medium truncate max-w-[200px]">{data.artwork.filename}</p>
              <p className="text-[10px] text-muted-foreground">
                {(data.artwork.size / 1024).toFixed(0)} KB — {new Date(data.artwork.created_at).toLocaleDateString('es-MX')}
                {data.print_side && ` — Cara: ${FACE_LABELS[data.print_side] || data.print_side}`}
              </p>
            </div>
          </div>
          <div className="flex gap-1.5">
            {data.artwork.content_type?.startsWith('image/') && (
              <Button size="sm" variant="ghost" className="h-7 text-xs gap-1"
                onClick={() => setShowArtworkModal(true)} data-testid="btn-preview-artwork">
                <Eye className="w-3 h-3" /> Ver
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 text-xs gap-1"
              onClick={handleDownloadArtwork} disabled={downloading === 'artwork'}
              data-testid="btn-download-artwork">
              {downloading === 'artwork' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
              Original
            </Button>
          </div>
        </div>
      )}

      {/* Dieline Preview */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-muted-foreground">Preview Dieline 2D</span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
              onClick={() => setZoom(z => Math.max(0.3, z - 0.2))} data-testid="btn-zoom-out">
              <ZoomOut className="w-3.5 h-3.5" />
            </Button>
            <span className="text-[10px] text-muted-foreground w-10 text-center font-mono">
              {Math.round(zoom * 100)}%
            </span>
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
              onClick={() => setZoom(z => Math.min(4, z + 0.2))} data-testid="btn-zoom-in">
              <ZoomIn className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
              onClick={() => { setZoom(1); }} data-testid="btn-zoom-reset">
              <RotateCcw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        <DielineSVG data={data.dieline} artworkUrl={artworkUrl} zoom={zoom} onZoomChange={setZoom} />
      </div>

      {/* Download Buttons */}
      <div className="grid grid-cols-3 gap-2">
        <Button size="sm" variant="outline" className="h-9 text-xs gap-1.5"
          onClick={() => handleDownload('pdf')} disabled={!!downloading}
          data-testid="btn-download-pdf">
          {downloading === 'pdf' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 text-red-500" />}
          PDF Producción
        </Button>
        <Button size="sm" variant="outline" className="h-9 text-xs gap-1.5"
          onClick={() => handleDownload('svg')} disabled={!!downloading}
          data-testid="btn-download-svg">
          {downloading === 'svg' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileImage className="w-3.5 h-3.5 text-blue-500" />}
          SVG (Corel)
        </Button>
        <Button size="sm" variant="outline" className="h-9 text-xs gap-1.5"
          onClick={() => handleDownload('png')} disabled={!!downloading}
          data-testid="btn-download-png">
          {downloading === 'png' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5 text-green-500" />}
          PNG Preview
        </Button>
      </div>

      {/* Artwork Preview Modal */}
      {showArtworkModal && artworkUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setShowArtworkModal(false)} data-testid="artwork-preview-modal">
          <div className="relative max-w-3xl max-h-[80vh] p-2 bg-white rounded-xl shadow-2xl"
            onClick={e => e.stopPropagation()}>
            <button className="absolute -top-3 -right-3 w-7 h-7 bg-white rounded-full shadow flex items-center justify-center text-sm font-bold"
              onClick={() => setShowArtworkModal(false)} data-testid="btn-close-artwork-modal">
              ×
            </button>
            <img src={artworkUrl} alt="Artwork" className="max-w-full max-h-[75vh] rounded object-contain" />
            <p className="text-center text-xs text-muted-foreground mt-2">{data.artwork?.filename}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtworkDieline;
